/**
 * Данные для оперативной карты диспетчера (`/admin/dispatch-map`).
 *
 * Тянем:
 *  * заказы в SEARCH или в активных статусах (с lat/lon — без координат
 *    маркер не нарисовать);
 *  * инспекторов с свежим/устаревшим last_geog (без позиции — не рисуем).
 *
 * Активный заказ инспектора — отдельным запросом по `inspector_id IN (...)`,
 * как в `inspectors_positions` (FK orders.inspector_id указывает на
 * auth.users, embedded JOIN не работает).
 */
import { createClient } from '@/api/supabase'
import { ACTIVE_INSPECTOR_STATUSES } from '@/api/inspectors_positions'

export interface DispatchOrder {
  id: string
  brand: string
  model: string
  desired_price: number | null
  final_price: number | null
  address: string
  status: string
  inspector_id: string | null
  lat: number
  lon: number
  created_at: string
}

export interface DispatchInspector {
  id: string
  full_name: string | null
  phone: string | null
  last_lat: number
  last_lon: number
  last_address: string | null
  last_position_at: string
  active_order: { id: string; status: string } | null
}

export interface DispatchMapData {
  orders: DispatchOrder[]
  inspectors: DispatchInspector[]
}

export async function loadDispatchMapData(): Promise<DispatchMapData> {
  const supabase = createClient()

  const orderStatuses = ['SEARCH', ...ACTIVE_INSPECTOR_STATUSES] as string[]

  const [ordersRes, inspectorsRes] = await Promise.all([
    supabase
      .from('orders')
      .select(
        'id, brand, model, desired_price, final_price, address, status, inspector_id, lat, lon, created_at',
      )
      .in('status', orderStatuses)
      .not('lat', 'is', null)
      .not('lon', 'is', null),
    supabase
      .from('profiles')
      .select(
        'id, full_name, phone, last_lat, last_lon, last_address, last_position_at',
      )
      .eq('role', 'inspector')
      .eq('is_active', true)
      .not('last_lat', 'is', null)
      .not('last_lon', 'is', null),
  ])
  if (ordersRes.error) throw new Error(ordersRes.error.message)
  if (inspectorsRes.error) throw new Error(inspectorsRes.error.message)

  const orders = (ordersRes.data ?? []) as DispatchOrder[]
  const inspectorRows = (inspectorsRes.data ?? []) as Array<
    Omit<DispatchInspector, 'active_order'>
  >

  // Активные заказы инспекторов (для маркировки «занят» и постоянных линий).
  const inspectorIds = inspectorRows.map((p) => p.id)
  let activeByInspector = new Map<string, { id: string; status: string }>()
  if (inspectorIds.length > 0) {
    const { data: activeRows, error: activeErr } = await supabase
      .from('orders')
      .select('id, inspector_id, status')
      .in('inspector_id', inspectorIds)
      .in('status', ACTIVE_INSPECTOR_STATUSES as unknown as string[])
    if (activeErr) throw new Error(activeErr.message)
    activeByInspector = new Map(
      (activeRows ?? []).map((o: { id: string; inspector_id: string; status: string }) => [
        o.inspector_id,
        { id: o.id, status: o.status },
      ]),
    )
  }

  const inspectors: DispatchInspector[] = inspectorRows.map((p) => ({
    ...p,
    last_position_at: p.last_position_at,
    active_order: activeByInspector.get(p.id) ?? null,
  }))

  return { orders, inspectors }
}

/**
 * Фильтр «активный заказ инспектора» — те же статусы, что и в
 * `idx_one_active_order_per_inspector` (БД) и `kInspectorActiveOrderStatuses`
 * (мобилка).
 */
export function isOrderActive(status: string): boolean {
  return (ACTIVE_INSPECTOR_STATUSES as readonly string[]).includes(status)
}
